/**
 * @fileoverview Office phone, extension, and secondary phone fields for Contact Information.
 */

import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import { IconButton } from "@mui/material";
import CountryCodeSelector from "../../CountryCodeSelector";
import {  getCountryCodeOptions  } from "../../../data/selectOptions";
import { getSelectedCountryCodeOption } from "../../../utils/common";
import styles from "./ContactInformation.module.css";

/** Translation key for "show in profile" toggle label */
const SHOW_IN_PROFILE_KEY = "components.contactInformation.showInProfile";

export function OfficePhoneField({ section }) {
  const {
    t,
    isTablet,
    telefonoDeOficina,
    setTelefonoDeOficina,
    selectedCountryCode,
    setSelectedCountryCode,
    getFieldValidationState,
    getErrorMessage,
    handleInputChange,
    handleValidatedFieldChange,
    showOfficePhone,
    setShowOfficePhone,
    handleToggleChange,
  } = section;
  const countryCodeOptions = getCountryCodeOptions(t);

  return (
    <div className={styles.inputGroup}>
      <label className={styles.inputLabel}>
        {t("components.contactInformation.officePhoneLabel")}
      </label>
      <div
        className={`${styles.phoneInputContainer} ${
          getFieldValidationState("telefonoDeOficina", telefonoDeOficina) ===
          "error"
            ? styles.inputError
            : ""
        }`}
      >
        <CountryCodeSelector
          value={getSelectedCountryCodeOption(
            countryCodeOptions,
            selectedCountryCode,
          )}
          onChange={(value) => {
            setSelectedCountryCode(value.value);
            handleInputChange("telefonoDeOficinaCodigoPais", value.value);
          }}
          containerWidth={isTablet ? "85%" : "115%"}
          placeholder={t(
            "components.contactInformation.countryCodeSelectorPlaceholder",
          )}
        />
        <input
          type="tel"
          className={styles.input}
          placeholder={t(
            "components.contactInformation.officePhonePlaceholder",
          )}
          value={telefonoDeOficina}
          onChange={(e) => {
            const value = e.target.value;
            setTelefonoDeOficina(value);
            handleValidatedFieldChange("telefonoDeOficina", value);
          }}
        />
      </div>
      {getErrorMessage("telefonoDeOficina") && (
        <p className={styles.errorMessage}>
          {getErrorMessage("telefonoDeOficina")}
        </p>
      )}
      <div className={styles.showInProfileContainer}>
        <span className={styles.showInProfileText}>
          {t(SHOW_IN_PROFILE_KEY)}
        </span>
        <IconButton
          onClick={() => {
            setShowOfficePhone(!showOfficePhone);
            handleToggleChange("mostrarTelefonoDeOficina", !showOfficePhone);
          }}
          size="small"
          className={styles.toggleButton}
          sx={{
            "& .MuiSvgIcon-root": {
              color: showOfficePhone ? "#1d5ae8" : "#C7C8C8",
            },
          }}
        >
          {showOfficePhone ? <ToggleOnIcon color="primary" /> : <ToggleOffIcon />}
        </IconButton>
      </div>
    </div>
  );
}

export function ExtensionPhoneField({ section }) {
  const {
    t,
    extensionTelefono,
    setExtensionTelefono,
    getFieldValidationState,
    getErrorMessage,
    handleValidatedFieldChange,
    showExtension,
    setShowExtension,
    handleToggleChange,
  } = section;

  return (
    <div className={styles.inputGroup}>
      <label className={styles.inputLabel}>
        {t("components.contactInformation.extensionLabel")}
      </label>
      <input
        type="number"
        className={`${styles.input} ${
          getFieldValidationState("extensionTelefono", extensionTelefono) ===
          "error"
            ? styles.inputError
            : ""
        }`}
        placeholder={t("components.contactInformation.extensionPlaceholder")}
        value={extensionTelefono}
        onChange={(e) => {
          const value = e.target.value;
          if (value.length <= 5) {
            setExtensionTelefono(value);
            handleValidatedFieldChange("extensionTelefono", value);
          }
        }}
      />
      {getErrorMessage("extensionTelefono") && (
        <p className={styles.errorMessage}>
          {getErrorMessage("extensionTelefono")}
        </p>
      )}
      <div className={styles.showInProfileContainer}>
        <span className={styles.showInProfileText}>
          {t(SHOW_IN_PROFILE_KEY)}
        </span>
        <IconButton
          onClick={() => {
            setShowExtension(!showExtension);
            handleToggleChange("mostrarExtensionTelefono", !showExtension);
          }}
          size="small"
          className={styles.toggleButton}
          sx={{
            "& .MuiSvgIcon-root": {
              color: showExtension ? "#1d5ae8" : "#C7C8C8",
            },
          }}
        >
          {showExtension ? <ToggleOnIcon color="primary" /> : <ToggleOffIcon />}
        </IconButton>
      </div>
    </div>
  );
}

export function SecondaryPhoneField({ section }) {
  const {
    t,
    isTablet,
    telefonoSecundario,
    setTelefonoSecundario,
    selectedSecondaryCountryCode,
    setSelectedSecondaryCountryCode,
    getFieldValidationState,
    getErrorMessage,
    handleInputChange,
    handleValidatedFieldChange,
    showSecondaryPhone,
    setShowSecondaryPhone,
    handleToggleChange,
  } = section;
  const countryCodeOptions = getCountryCodeOptions(t);

  return (
    <div className={styles.inputGroup}>
      <label className={styles.inputLabel}>
        {t("components.contactInformation.secondaryPhoneLabel")}
      </label>
      <div
        className={`${styles.phoneInputContainer} ${
          getFieldValidationState(
            "telefonoSecundario",
            telefonoSecundario,
          ) === "error"
            ? styles.inputError
            : ""
        }`}
      >
        <CountryCodeSelector
          value={getSelectedCountryCodeOption(
            countryCodeOptions,
            selectedSecondaryCountryCode,
          )}
          onChange={(value) => {
            setSelectedSecondaryCountryCode(value.value);
            handleInputChange("telefonoSecundarioCodigoPais", value.value);
          }}
          containerWidth={isTablet ? "85%" : "115%"}
          placeholder={t(
            "components.contactInformation.countryCodeSelectorPlaceholder",
          )}
        />
        <input
          type="tel"
          className={styles.input}
          placeholder={t(
            "components.contactInformation.secondaryPhonePlaceholder",
          )}
          value={telefonoSecundario}
          onChange={(e) => {
            const value = e.target.value;
            setTelefonoSecundario(value);
            handleValidatedFieldChange("telefonoSecundario", value);
          }}
        />
      </div>
      {getErrorMessage("telefonoSecundario") && (
        <p className={styles.errorMessage}>
          {getErrorMessage("telefonoSecundario")}
        </p>
      )}
      <div className={styles.showInProfileContainer}>
        <span className={styles.showInProfileText}>
          {t(SHOW_IN_PROFILE_KEY)}
        </span>
        <IconButton
          onClick={() => {
            setShowSecondaryPhone(!showSecondaryPhone);
            handleToggleChange(
              "mostrarTelefonoSecundario",
              !showSecondaryPhone,
            );
          }}
          size="small"
          className={styles.toggleButton}
          sx={{
            "& .MuiSvgIcon-root": {
              color: showSecondaryPhone ? "#1d5ae8" : "#C7C8C8",
            },
          }}
        >
          {showSecondaryPhone ? (
            <ToggleOnIcon color="primary" />
          ) : (
            <ToggleOffIcon />
          )}
        </IconButton>
      </div>
    </div>
  );
}
